import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import {
  useCreateEducationItemMutation,
  useGetEducationItemBySlugQuery,
  useUpdateEducationItemMutation,
} from "../../services/education/education";
import { EducationItem } from "../../types/types";
import { onlyNumbers, toStringSafe } from "../shared/utils";
import { EDUCATION_ITEM_FORM_TEXTS, INPUT_LABEL_PROPS } from "../shared/constants";
import {
  CommonBoxStyles,
  FormFieldGrid,
  FormFieldGridResponsive,
  SaveActionBar,
} from "../shared/styledComponents";

const T = EDUCATION_ITEM_FORM_TEXTS;

const STATUS_OPTIONS: { value: EducationItem["status"]; label: string }[] = [
  { value: "active", label: T.statusActive },
  { value: "inactive", label: T.statusInactive },
  { value: "draft", label: T.statusDraft },
];

const CATEGORY_OPTIONS: { value: EducationItem["category"]; label: string }[] = [
  { value: "program", label: T.categoryProgram },
  { value: "course", label: T.categoryCourse },
];

type Props = {
  // In "edit" mode `slug` identifies the item to load; in "create" mode it's omitted.
  mode: "create" | "edit";
  slug?: string;
};

const EducationItemForm: React.FC<Props> = ({ mode, slug }) => {
  const navigate = useNavigate();
  const isEdit = mode === "edit";

  const {
    data: existing,
    isFetching,
    isError,
  } = useGetEducationItemBySlugQuery(slug ?? "", { skip: !isEdit || !slug });

  const [createEducationItem, { isLoading: isCreating }] = useCreateEducationItemMutation();
  const [updateEducationItem, { isLoading: isUpdating }] = useUpdateEducationItemMutation();

  const initial = useMemo(() => {
    const src = existing ?? ({} as EducationItem);
    return {
      slug: src.slug ?? "",
      id: toStringSafe(src.id),
      name: src.name ?? "",
      description: src.description ?? "",
      status: (src.status ?? "draft") as EducationItem["status"],
      category: (src.category ?? "course") as EducationItem["category"],
      start_date: src.start_date ?? "",
      end_date: src.end_date ?? "",
      duration: src.duration ?? "",
      max_capacity: toStringSafe(src.max_capacity),
      picture: src.picture ?? "",
    };
  }, [existing]);

  const [form, setForm] = useState(initial);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    setForm(initial);
  }, [initial]);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSave = async (): Promise<void> => {
    setErrorMsg(null);

    if (!form.slug.trim() || !form.id.trim() || !form.name.trim()) {
      setErrorMsg(T.requiredFields);
      return;
    }
    const idNumber = Number(form.id);
    const capacityNumber = form.max_capacity ? Number(form.max_capacity) : 0;
    if (Number.isNaN(idNumber) || Number.isNaN(capacityNumber)) {
      setErrorMsg(T.numericError);
      return;
    }

    const payload: EducationItem = {
      slug: form.slug.trim(),
      id: idNumber,
      name: form.name.trim(),
      description: form.description || null,
      status: form.status,
      category: form.category,
      start_date: form.start_date || null,
      end_date: form.end_date || null,
      duration: form.duration || null,
      max_capacity: capacityNumber,
      picture: form.picture || null,
      images: existing?.images ?? [],
    };

    try {
      if (isEdit && slug) {
        await updateEducationItem({ slug, patch: payload }).unwrap();
      } else {
        await createEducationItem(payload).unwrap();
      }
      navigate("/education-item-maintenance");
    } catch {
      setErrorMsg(T.saveError);
    }
  };

  if (isEdit && isFetching && !existing) {
    return (
      <CommonBoxStyles p={2}>
        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
          {T.loading}
        </Typography>
      </CommonBoxStyles>
    );
  }

  if (isEdit && (isError || !existing)) {
    return (
      <CommonBoxStyles p={2}>
        <Typography variant="subtitle2" fontWeight="bold" color="text.primary">
          {T.notFound}
        </Typography>
      </CommonBoxStyles>
    );
  }

  const isSaving = isCreating || isUpdating;

  return (
    <CommonBoxStyles>
      <Box m={2} p={2} sx={{ background: "white", borderRadius: 1 }}>
        <Typography variant="h6" fontWeight="bold" color="text.primary" mb={2}>
          {isEdit ? T.editTitle : T.createTitle}
        </Typography>

        {errorMsg && (
          <Box mb={2}>
            <Alert severity="error">{errorMsg}</Alert>
          </Box>
        )}

        <FormFieldGrid>
          <TextField
            label={T.slugLabel}
            value={form.slug}
            onChange={(e) => set("slug", e.target.value)}
            size="small"
            fullWidth
            // Slug is the DynamoDB partition key — immutable once created.
            disabled={isEdit}
            InputLabelProps={INPUT_LABEL_PROPS}
          />
          <TextField
            label={T.idLabel}
            value={form.id}
            onChange={(e) => set("id", onlyNumbers(e.target.value))}
            size="small"
            fullWidth
            inputMode="numeric"
            InputLabelProps={INPUT_LABEL_PROPS}
          />
          <TextField
            label={T.nameLabel}
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={INPUT_LABEL_PROPS}
          />
        </FormFieldGrid>

        <Box mt={2}>
          <TextField
            label={T.descriptionLabel}
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            InputLabelProps={INPUT_LABEL_PROPS}
          />
        </Box>

        <FormFieldGridResponsive
          sx={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
        >
          <TextField
            select
            label={T.statusLabel}
            value={form.status}
            onChange={(e) => set("status", e.target.value as EducationItem["status"])}
            size="small"
            fullWidth
            InputLabelProps={INPUT_LABEL_PROPS}
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={T.categoryLabel}
            value={form.category}
            onChange={(e) => set("category", e.target.value as EducationItem["category"])}
            size="small"
            fullWidth
            InputLabelProps={INPUT_LABEL_PROPS}
          >
            {CATEGORY_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={T.maxCapacityLabel}
            value={form.max_capacity}
            onChange={(e) => set("max_capacity", onlyNumbers(e.target.value))}
            size="small"
            fullWidth
            inputMode="numeric"
            InputLabelProps={INPUT_LABEL_PROPS}
          />
        </FormFieldGridResponsive>

        <FormFieldGridResponsive
          sx={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))" }}
        >
          <TextField
            label={T.startDateLabel}
            type="date"
            value={form.start_date}
            onChange={(e) => set("start_date", e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={T.endDateLabel}
            type="date"
            value={form.end_date}
            onChange={(e) => set("end_date", e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label={T.durationLabel}
            value={form.duration}
            onChange={(e) => set("duration", e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={INPUT_LABEL_PROPS}
          />
        </FormFieldGridResponsive>

        <Box mt={2}>
          <TextField
            label={T.pictureLabel}
            value={form.picture}
            onChange={(e) => set("picture", e.target.value)}
            size="small"
            fullWidth
            InputLabelProps={INPUT_LABEL_PROPS}
          />
        </Box>

        <SaveActionBar>
          <Button
            onClick={() => void handleSave()}
            disabled={isSaving}
            variant="outlined"
            color="success"
            startIcon={<SaveIcon />}
            fullWidth
            sx={{ fontWeight: "bold", boxShadow: 2, maxWidth: "40%", bgcolor: "white" }}
          >
            {isSaving ? T.saving : isEdit ? T.save : T.createButton}
          </Button>
        </SaveActionBar>
      </Box>
    </CommonBoxStyles>
  );
};

export default EducationItemForm;
